/* ═══════════════════════════════════════════════════════════════════════════
 * HISTORIQUE DE FACTURATION SaaS — UN ECRAN PAR MOIS
 * ═══════════════════════════════════════════════════════════════════════════
 * Decision de Dom, 11/09/2026 : « L'historique devient possible des que les
 * factures existent. Le modele est deja la et eprouve. Un ecran par mois, la
 * facture FIGEE servie depuis son stockage, JAMAIS regeneree. »
 *
 * ⚠️ CET ECRAN NE CALCULE AUCUN MONTANT. Il lit ce que la piece PORTE :
 *    `total_ttc`, `total_ht`, `montant_tva`, `nb_agents_actifs`, `tarif_unitaire`
 *    sont FIGES a l'emission. Recalculer depuis la grille du jour donnerait un
 *    autre chiffre au premier changement de tarif — et deux verites pour une
 *    facture, c'est la facture qui perd (regle 12 : une facture emise ne rejoue
 *    jamais le referentiel).
 *
 * ⚠️ LE PDF EST SERVI, PAS FABRIQUE. La route rend le fichier fige depuis son
 *    stockage ; si la ligne designe un fichier absent, elle repond
 *    410 DOCUMENT_FIGE_ABSENT et ne regenere RIEN — « une piece perdue se
 *    signale, elle ne se refabrique pas ». Cet ecran RELAIE ce refus tel quel
 *    au lieu de le traduire en « erreur » : le message porte le numero de la
 *    facture et dit quoi faire.
 *
 * ⚠️ UNE FACTURE ANNULEE RESTE VISIBLE ICI. Elle sort des CALCULS, jamais des
 *    DONNEES (regle 39) : c'est une piece, et la trace d'une decision. Elle
 *    porte son motif, qui repondra le jour ou l'on demandera pourquoi un mois
 *    est vide.
 * ═══════════════════════════════════════════════════════════════════════════ */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from 'services/api';

function saApi() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('gss_sa_token')}` } };
}

const MOIS = ['', 'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const STATUT_COLORS = {
  payee: 'var(--gss-success)', en_attente: 'var(--gss-warning)',
  retard: 'var(--gss-danger)', annulee: 'var(--gss-text-light)',
};
const STATUT_LABELS = {
  payee: 'Payee', en_attente: 'En attente', retard: 'En retard', annulee: 'Annulee',
};

export default function SuperAdminFacturesHistorique() {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    api.get('/super-admin/factures', saApi())
      .then(r => setFactures(r.data))
      .catch(() => setErreur('Lecture des factures impossible.'))
      .finally(() => setLoading(false));
  }, []);

  /* ⚠️ LE REFUS DU SERVEUR EST RELAYE, PAS TRADUIT. Un `alert('Erreur PDF')`
     effacerait la seule information utile : que la piece est REFERENCEE EN BASE
     mais ABSENTE DU STOCKAGE — ce qui se repare, et qui n'est pas la meme chose
     qu'un defaut de generation. */
  const ouvrirPdf = async (f) => {
    try {
      const r = await api.get(`/super-admin/factures/${f.id}/pdf`,
        { ...saApi(), responseType: 'arraybuffer' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${f.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const st = err && err.response && err.response.status;
      if (st === 410) {
        alert(`Le document fige de la facture ${f.numero} est reference en base `
          + `mais absent du stockage. Il n'est PAS regenere : une piece perdue se `
          + `signale, elle ne se refabrique pas. A restaurer depuis la sauvegarde.`);
      } else {
        alert(`Lecture du document de ${f.numero} impossible (statut ${st || 'inconnu'}).`);
      }
    }
  };

  // Regroupement par mois, du plus recent au plus ancien.
  const parMois = [];
  const index = new Map();
  [...factures]
    .sort((a, b) => (b.annee - a.annee) || (b.mois - a.mois) || String(a.numero).localeCompare(String(b.numero)))
    .forEach((f) => {
      const cle = `${f.annee}-${String(f.mois).padStart(2, '0')}`;
      if (!index.has(cle)) { index.set(cle, { cle, annee: f.annee, mois: f.mois, lignes: [] }); parMois.push(index.get(cle)); }
      index.get(cle).lignes.push(f);
    });

  const fmt = (n) => Number(n || 0).toLocaleString();

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: 'var(--gss-red)', letterSpacing: 4, textTransform: 'uppercase' }}>Super Admin</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--gss-text)' }}>Historique de facturation</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <Link to="/super-admin/facturation" style={S.navBtn}>Facturation</Link>
          <Link to="/super-admin" style={S.navBtn}>Tableau de bord</Link>
        </div>
      </div>

      {loading && <div style={S.card}>Chargement…</div>}
      {erreur && <div style={{ ...S.card, color: 'var(--gss-danger)' }}>{erreur}</div>}

      {!loading && !erreur && parMois.length === 0 && (
        /* ⚠️ UN VIDE SE DIT, il ne se laisse pas deviner. Un ecran blanc et une
            absence de facture se ressemblent — et l'un est une panne. */
        <div style={S.card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Aucune facture emise a ce jour.</div>
          <div style={{ fontSize: 12, color: 'var(--gss-text-light)', lineHeight: 1.6 }}>
            Ce n&apos;est pas une absence de donnee, c&apos;est une absence de piece.
            Les societes du groupe editeur et les tenants de demonstration ne sont
            jamais factures : tant qu&apos;aucun client facturable n&apos;existe, la
            generation mensuelle tourne et ne produit rien.
          </div>
        </div>
      )}

      {parMois.map((m) => {
        const retenues = m.lignes.filter(f => f.statut !== 'annulee' && !f.tenant_interne && !f.est_demonstration);
        const totalMois = retenues.reduce((s, f) => s + Number(f.total_ttc || 0), 0);
        const nbIntra = m.lignes.filter(f => f.tenant_interne || f.est_demonstration).length;
        const nbAnn = m.lignes.filter(f => f.statut === 'annulee').length;
        return (
          <div key={m.cle} style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--gss-text)' }}>
                {MOIS[m.mois]} {m.annee}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gss-text-light)' }}>
                {m.lignes.length} piece{m.lignes.length > 1 ? 's' : ''} · <strong>{fmt(totalMois)} F</strong> facture(s) client
                {nbIntra > 0 && <> · {nbIntra} intragroupe hors total</>}
                {nbAnn > 0 && <> · {nbAnn} annulee{nbAnn > 1 ? 's' : ''} hors total</>}
              </div>
            </div>

            <div className="gss-table-wrap">
              <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['N°', 'Societe', 'Emise le', 'Agents', 'Total TTC', 'Statut', 'Document'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {m.lignes.map((f) => (
                    <tr key={f.id} style={{ opacity: f.statut === 'annulee' ? 0.6 : 1 }}>
                      <td style={{ ...S.td, fontWeight: 700 }}>{f.numero}</td>
                      <td style={S.td}>
                        {f.tenant_nom}
                        {(f.tenant_interne || f.est_demonstration) && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--gss-text-light)' }}>
                            ({f.tenant_interne ? 'groupe editeur' : 'demonstration'})
                          </span>
                        )}
                      </td>
                      <td style={S.td}>{f.date_emission ? String(f.date_emission).slice(0, 10) : '—'}</td>
                      <td style={{ ...S.td, textAlign: 'right' }}>{f.nb_agents_actifs}</td>
                      <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{fmt(f.total_ttc)}</td>
                      <td style={S.td}>
                        <span style={{ color: STATUT_COLORS[f.statut] || 'var(--gss-text)', fontWeight: 700, fontSize: 12 }}>
                          {STATUT_LABELS[f.statut] || f.statut}
                        </span>
                        {f.statut === 'annulee' && f.motif_annulation && (
                          <div style={{ fontSize: 10, color: 'var(--gss-text-light)', maxWidth: 320 }}>{f.motif_annulation}</div>
                        )}
                      </td>
                      <td style={S.td}>
                        {/* ⚠️ LE BOUTON NE S'AFFICHE QUE SI LA LIGNE DESIGNE UN
                            DOCUMENT. Proposer « Telecharger » sur une facture qui
                            n'en a pas, c'est promettre une piece qui n'existe pas. */}
                        {f.document_url ? (
                          <button type="button" onClick={() => ouvrirPdf(f)} style={S.lien}>
                            Telecharger
                          </button>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--gss-text-light)' }}>aucun document fige</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const S = {
  page: { width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: 'var(--gss-bg)', padding: 'clamp(12px, 3vw, 28px)' },
  card: { background: 'var(--gss-white)', borderRadius: 12, padding: 20, border: '1px solid var(--gss-border)', minWidth: 0 },
  navBtn: { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--gss-border)', color: 'var(--gss-text)', textDecoration: 'none', fontSize: 13, fontWeight: 600 },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: 10, color: 'var(--gss-text-light)', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid var(--gss-border)', whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', fontSize: 13, color: 'var(--gss-text)', borderBottom: '1px solid var(--gss-border)' },
  lien: { background: 'none', border: 'none', color: 'var(--gss-red)', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' },
};
